import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    faArrowRightFromBracket,
    faArrowRightToBracket,
    faArrowRotateLeft,
    faBook,
    faBoxesStacked,
    faCalendarCirclePlus,
    faChartSimple,
    faCircleQuestion,
    faClipboardList,
    faCommentsAlt,
    faDiamondExclamation,
    faFileMagnifyingGlass,
    faFolderOpen,
    faGear,
    faMessage,
    faPlugCircleBolt,
    faQuestionCircle,
    faSplit,
    faTruck,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery as useReactQuery } from '@tanstack/react-query';

import { useHubSpotChat } from '@spacefill/shared/src/hooks/useHubSpotChat';
import { call } from '@spacefill/shared/src/utils/Backend';
import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { getHubSpotContactFormUrl } from '@spacefill/shared/src/utils/SupportContactFormUrl';
import { useBrandPreviewContext } from '@spacefill/shared/src/utils/contexts/BrandContext';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Sidebar } from '@spacefill/uikit/src/components/Sidebar';

import { AdminCompanySwitcher } from './UserSwitcher';

const AppSidebar = () => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const location = useLocation();
    const { brandPreview } = useBrandPreviewContext();

    const appBranding = {
        ...(user?.branding || {}),
        ...brandPreview,
    };

    const isCustomer = !!user.customerId;
    const isProvider = !!user.logisticProviderId;
    const isCarrier = !!user.carrierId;

    const shipperCanShowAppointments =
        isCustomer && user.logisticProviders.some((lsp) => lsp.settings?.appointmentScheduling?.enabled);

    const providerCanShowAppointments = isProvider && user.logisticProvider?.settings?.appointmentScheduling?.enabled;

    const { toggleHubSpotChat } = useHubSpotChat({ enable: getEnvironmentVariable('mode') !== 'development' });

    const { data: pdfConfigs, isLoading: isLoadingPdfConfigs } = useReactQuery({
        queryKey: ['email-to-order/pdf-configuration/getAll'],
        queryFn: () => call('email-to-order/pdf-configuration/getAll', {}, 'POST'),
        placeholderData: [],
        enabled: !isCustomer,
    });

    const shouldFetchCount = !!user.warehouseIds.length && !isLoadingPdfConfigs && pdfConfigs.length > 0;

    const { data: countExits } = useReactQuery({
        queryKey: ['unconfirmedorders/getAll', 'EXIT'],
        queryFn: () => call('/unconfirmedorders/getAll', { filterBy: { type: { in: ['EXIT'] } } }, 'POST'),
        placeholderData: {},
        enabled: shouldFetchCount,
    });

    const { data: countEntries } = useReactQuery({
        queryKey: ['unconfirmedorders/getAll', 'ENTRY'],
        queryFn: () => call('/unconfirmedorders/getAll', { filterBy: { type: { in: ['ENTRY'] } } }, 'POST'),
        placeholderData: {},
        enabled: shouldFetchCount,
    });

    const { data: unconfirmedTransports } = useReactQuery({
        queryKey: ['unconfirmed-transports/get-all'],
        queryFn: async () => {
            return await call('/unconfirmed-transports/get-all', {}, 'GET');
        },
        enabled: !!user.logisticProvider.id && user?.isTransportOrdersManagementEnabled,
        placeholderData: [],
    });

    return (
        <Fragment>
            <Sidebar
                staffDebugTools={user.isStaff && <AdminCompanySwitcher />}
                logoUrl={appBranding?.logoUrl}
                mainNavigation={[
                    // PILOTAGE
                    {
                        sectionTitle: t('Sidebar section steering'),
                        sectionDataTestid: 'nav-section-steering',
                        items: [
                            {
                                title: t('Dashboards'),
                                icon: <FontAwesomeIcon icon={faChartSimple} />,
                                isActive: location.pathname.includes('/analytics/'),
                                to: '/analytics/',
                                dataTestid: 'nav-dashboards',
                                conditionForDisplay: () => !isCarrier && !user.features.disableAnalytics,
                            },
                            {
                                title: t('Planning'),
                                icon: <FontAwesomeIcon icon={faCalendarCirclePlus} />,
                                isActive: location.pathname.includes('/appointments/list/'),
                                to: '/appointments/list/',
                                dataTestid: 'nav-appointments',
                                conditionForDisplay: () => shipperCanShowAppointments,
                            },
                            {
                                title: t('Planning'),
                                icon: <FontAwesomeIcon icon={faCalendarCirclePlus} />,
                                isActive: !!location.pathname.match(
                                    /\/appointments\/(warehouses\/[a-f0-9-]+\/)?(calendar|list)\/?/
                                ),
                                to: '/appointments/calendar/',
                                dataTestid: 'nav-appointments',
                                conditionForDisplay: () => providerCanShowAppointments,
                            },
                        ],
                    },

                    // VENTES / FLUX SORTANT
                    {
                        sectionTitle: isCustomer ? t('Sidebar section sales') : t('Sidebar section outbound flow'),
                        sectionDataTestid: 'nav-section-sales',
                        items: [
                            {
                                title: t('Sales list'),
                                icon: <FontAwesomeIcon icon={faArrowRightFromBracket} />,
                                isActive: location.pathname.startsWith('/sales/'),
                                to: '/sales/',
                                dataTestid: 'nav-sales-orders',
                                conditionForDisplay: () => user.features.automations.orderRoutingEnabled,
                            },
                            {
                                title: t('Exit_plural'),
                                icon: <FontAwesomeIcon icon={faArrowRightFromBracket} />,
                                isActive: !!location.pathname.match(
                                    /\/(logistic-management\/)?(warehouses\/[a-f0-9-]+\/)?exit-orders\/?/
                                ),
                                to: '/logistic-management/exit-orders/',
                                dataTestid: 'nav-exit-orders',
                                conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                            },
                            {
                                title: t('Exits to review'),
                                icon: <FontAwesomeIcon icon={faFileMagnifyingGlass} />,
                                isActive: location.pathname.includes('/logistic-management/unconfirmed-exits/'),
                                to: '/logistic-management/unconfirmed-exits/',
                                countValue: countExits ? countExits[1] : undefined,
                                dataTestid: 'storage-emails',
                                conditionForDisplay: () => {
                                    return pdfConfigs && pdfConfigs.length > 0 && isProvider;
                                },
                            },
                            {
                                title: t('Transports'),
                                icon: <FontAwesomeIcon icon={faTruck} />,
                                isActive: location.pathname.includes('/transports/'),
                                to: '/transports/',
                                dataTestid: 'nav-transports-orders',
                                conditionForDisplay: () => user?.isTransportOrdersManagementEnabled || isCarrier,
                            },
                            {
                                title: t('Transports to review'),
                                icon: <FontAwesomeIcon icon={faFileMagnifyingGlass} />,
                                isActive: location.pathname.includes('/unconfirmed-transports/'),
                                to: '/unconfirmed-transports/',
                                countValue: unconfirmedTransports[1],
                                dataTestid: 'nav-unconfirmed-transports-orders',
                                conditionForDisplay: () =>
                                    isProvider && unconfirmedTransports && user?.isTransportOrdersManagementEnabled,
                            },
                            {
                                title: t('Return_plural'),
                                icon: <FontAwesomeIcon icon={faArrowRotateLeft} />,
                                isActive: !!location.pathname.match(
                                    /\/(logistic-management\/)?(warehouses\/[a-f0-9-]+\/)?return-orders\/?/
                                ),
                                to: '/logistic-management/return-orders/',
                                dataTestid: 'nav-return-orders',
                                conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                            },
                        ],
                    },

                    // CHAÎNE D'APPROVISIONNEMENT / FLUX ENTRANT
                    {
                        sectionTitle: isCustomer
                            ? t('Sidebar section supply chain')
                            : t('Sidebar section inbound flow'),
                        sectionDataTestid: 'nav-section-supply-chain',
                        items: [
                            {
                                title: t('Entry_plural'),
                                icon: (
                                    <FontAwesomeIcon
                                        icon={faArrowRightToBracket}
                                        className='rotate-180'
                                    />
                                ),
                                isActive: !!location.pathname.match(
                                    /\/(logistic-management\/)?(warehouses\/[a-f0-9-]+\/)?entry-orders\/?/
                                ),
                                to: '/logistic-management/entry-orders/',
                                dataTestid: 'nav-entry-orders',
                                conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                            },
                            {
                                title: t('Entries to review'),
                                icon: <FontAwesomeIcon icon={faFileMagnifyingGlass} />,
                                isActive: location.pathname.includes('/logistic-management/unconfirmed-entries/'),
                                to: '/logistic-management/unconfirmed-entries/',
                                countValue: countEntries ? countEntries[1] : undefined,
                                dataTestid: 'storage-emails',
                                conditionForDisplay: () => {
                                    return pdfConfigs && pdfConfigs.length > 0 && isProvider;
                                },
                            },
                            {
                                title: t('Stock situation'),
                                icon: <FontAwesomeIcon icon={faBoxesStacked} />,
                                isActive: !!location.pathname.match(
                                    /\/logistic-management\/(warehouses\/[a-f0-9-]+\/)?stock-situation\/?/
                                ),
                                to: '/logistic-management/stock-situation/',
                                dataTestid: 'nav-stocks',
                                conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                            },
                        ],
                    },

                    // COLLABORATION
                    {
                        sectionTitle: t('Sidebar section collaboration'),
                        sectionDataTestid: 'nav-section-collaboration',
                        items: [
                            {
                                title: t('Incidents'),
                                icon: <FontAwesomeIcon icon={faDiamondExclamation} />,
                                isActive: !!location.pathname.match(/\/logistic-management\/incidents\/?/),
                                to: '/logistic-management/incidents/',
                                dataTestid: 'nav-incidents',
                                conditionForDisplay: () =>
                                    user.warehouseIds.length > 0 ||
                                    user?.isTransportOrdersManagementEnabled ||
                                    isCarrier,
                            },
                            {
                                title: t('Additional requests'),
                                icon: <FontAwesomeIcon icon={faClipboardList} />,
                                isActive: location.pathname.startsWith('/additional-requests/'),
                                to: '/additional-requests/',
                                dataTestid: 'nav-additional-requests',
                                conditionForDisplay: () => user.features.canUseAdditionalRequests,
                            },
                            {
                                title: t('Documents'),
                                icon: <FontAwesomeIcon icon={faFolderOpen} />,
                                isActive: location.pathname === '/documents/',
                                to: '/documents/',
                                dataTestid: 'nav-documents',
                                conditionForDisplay: () => user.warehouseIds.length > 0 || isCarrier,
                            },
                        ],
                    },

                    // GESTION
                    {
                        sectionTitle: t('Sidebar section management'),
                        sectionDataTestid: 'nav-section-management',
                        items: [
                            {
                                title: t('References'),
                                icon: <FontAwesomeIcon icon={faBook} />,
                                isActive: location.pathname.includes('/logistic-management/inventory/items/'),
                                to: '/logistic-management/inventory/items/',
                                dataTestid: 'nav-ref',
                                conditionForDisplay: () => user.warehouseIds.length > 0 && !isCarrier,
                            },
                            {
                                title: t('Connectivity'),
                                icon: <FontAwesomeIcon icon={faPlugCircleBolt} />,
                                isActive: location.pathname.startsWith('/logistic-management/integrations/'),
                                to: '/logistic-management/integrations/jobs',
                                dataTestid: 'nav-integrations',
                                conditionForDisplay: () => {
                                    return (
                                        (!!user?.logisticProvider?.settings?.integrationMonitoringEnabled ||
                                            !!user?.customer?.settings?.integrationMonitoringEnabled) &&
                                        !isCarrier
                                    );
                                },
                            },
                            {
                                title: t('Automations'),
                                icon: <FontAwesomeIcon icon={faSplit} />,
                                isActive: location.pathname === '/automations/',
                                to: '/automations/',
                                dataTestid: 'nav-automations',
                                conditionForDisplay: () => !isCarrier,
                            },
                            {
                                title: t('Settings'),
                                icon: <FontAwesomeIcon icon={faGear} />,
                                dataTestid: 'nav-settings',
                                isActive: location.pathname.startsWith('/settings/'),
                                to: '/settings/users/',
                            },
                        ],
                    },
                ]}
                bottomNavigation={[
                    {
                        title: t('Support'),
                        icon: <FontAwesomeIcon icon={faCircleQuestion} />,
                        subItems: [
                            {
                                title: t('Contact us'),
                                icon: <FontAwesomeIcon icon={faCommentsAlt} />,
                                href: getHubSpotContactFormUrl(user?.preferredLanguage, user?.email),
                                target: '_blank',
                            },
                            {
                                title: t('Help Center'),
                                icon: <FontAwesomeIcon icon={faQuestionCircle} />,
                                href:
                                    user?.preferredLanguage === 'FR'
                                        ? 'https://help.spacefill.eu/knowledge'
                                        : user?.preferredLanguage === 'DE'
                                          ? 'https://help.spacefill.eu/de/knowledge'
                                          : 'https://help.spacefill.eu/en/knowledge',
                                target: '_blank',
                            },
                            {
                                title: t('AI Assistant'),
                                icon: <FontAwesomeIcon icon={faMessage} />,
                                onClick: toggleHubSpotChat,
                                dataTestid: 'nav-chat',
                            },
                        ],
                    },
                ]}
            />
        </Fragment>
    );
};

export { AppSidebar };
